import { DependencyList, RefObject, useEffect, useState } from 'react'

const hasOverflow = (element: HTMLElement) =>
  element.scrollWidth - element.clientWidth > 1

export const useHorizontalOverflow = (
  ref: RefObject<HTMLElement | null>,
  dependencies: DependencyList = []
) => {
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const element = ref.current

    if (!element) {
      setIsOverflowing(false)
      return
    }

    let animationFrameId: number | null = null

    const evaluateOverflow = () => {
      const nextValue = hasOverflow(element)

      setIsOverflowing((prev) => (prev === nextValue ? prev : nextValue))
    }

    const scheduleEvaluate = () => {
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId)
      }

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null
        evaluateOverflow()
      })
    }

    evaluateOverflow()

    let resizeObserver: ResizeObserver | null = null

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(() => {
        scheduleEvaluate()
      })

      resizeObserver.observe(element)

      const content = element.firstElementChild
      if (content instanceof HTMLElement) {
        resizeObserver.observe(content)
      }
    }

    const mutationObserver =
      typeof MutationObserver === 'function'
        ? new MutationObserver(() => {
          scheduleEvaluate()
        })
        : null

    mutationObserver?.observe(element, { childList: true, subtree: true })

    const handleWindowResize = () => {
      scheduleEvaluate()
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleWindowResize)
      window.addEventListener('orientationchange', handleWindowResize)
    }

    return () => {
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()

      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleWindowResize)
        window.removeEventListener('orientationchange', handleWindowResize)
      }

      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [ref, ...dependencies])

  return isOverflowing
}
